import React from 'react';
import { T, gradeFor, isUngraded, UNGRADED } from '../../designTokens';
import { StatusBadge, thStyle, tdStyle } from '../Badges';
import { formatDate, formatTime } from '../../utils/date';
import type { ReportSummary } from '@privisy/api-types';
import type { ReportSortField, ReportSortOrder } from '../../utils/api';

/**
 * The score cell's text and colors. A report that assessed nothing
 * (isUngraded) shows N/A rather than "0 F". Shared with ReportCards.
 */
export function scoreBadge(report: Pick<ReportSummary, 'score' | 'overallStatus'>): { label: string; color: string; bg: string } {
  if (isUngraded(report.overallStatus)) return { label: UNGRADED.letter, color: UNGRADED.color, bg: UNGRADED.bg };
  const grade = gradeFor(report.score);
  return { label: `${report.score} ${grade.letter}`, color: grade.color, bg: grade.bg };
}

/** The path of a report's URL, or null when it is the site root. */
export function reportPath(url: string): string | null {
  try {
    const { pathname, search } = new URL(url);
    const path = `${pathname}${search}`;
    return path === '/' || path === '' ? null : path;
  } catch {
    return null;
  }
}

interface SortableHeaderProps {
  label: string;
  field: ReportSortField;
  sort?: ReportSortField;
  order: ReportSortOrder;
  onSort: (field: ReportSortField) => void;
  align?: 'left' | 'right';
}

function SortableHeader({ label, field, sort, order, onSort, align = 'left' }: SortableHeaderProps) {
  const active = sort === field;
  return (
    <th
      style={{ ...thStyle, textAlign: align }}
      aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        aria-label={`Sort by ${label.toLowerCase()}`}
        style={{
          font: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          color: active ? T.gray700 : T.gray400,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {label}
        <span aria-hidden="true" style={{ opacity: active ? 1 : 0.35 }}>
          {active ? (order === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

interface ReportsTableProps {
  reports: ReportSummary[];
  isAdmin?: boolean;
  showInitiatedBy: boolean;
  sort?: ReportSortField;
  order: ReportSortOrder;
  onSort: (field: ReportSortField) => void;
  selectedIds: Set<string>;
  onToggleOne: (id: string) => void;
  onToggleAll: () => void;
  onView: (id: string) => void;
  onShare: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}

export default function ReportsTable({
  reports,
  showInitiatedBy,
  sort,
  order,
  onSort,
  selectedIds,
  onToggleOne,
  onToggleAll,
  onView,
  onShare,
  onDelete,
}: ReportsTableProps) {
  const allSelected = reports.length > 0 && reports.every((r) => selectedIds.has(r.id));

  return (
    // A table cannot render narrower than its min-content width, so `width:
    // 100%` alone let it spill past the card and scroll the whole page.
    <div data-testid="reports-table-scroll" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr style={{ backgroundColor: T.gray50 }}>
            <th style={{ ...thStyle, width: '36px' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                aria-label="Select all reports on this page"
                style={{ cursor: 'pointer' }}
              />
            </th>
            <SortableHeader label="Report" field="domain" sort={sort} order={order} onSort={onSort} />
            <SortableHeader label="Score" field="score" sort={sort} order={order} onSort={onSort} />
            <th style={thStyle}>Status</th>
            {showInitiatedBy && <th style={thStyle}>Initiated By</th>}
            <SortableHeader label="Date" field="date" sort={sort} order={order} onSort={onSort} />
            <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => {
            const path = reportPath(report.url);
            const selected = selectedIds.has(report.id);
            const badge = scoreBadge(report);
            return (
              <tr
                key={report.id}
                style={{
                  borderBottom: `1px solid ${T.gray200}`,
                  backgroundColor: selected ? T.cobalt50 : undefined,
                }}
              >
                <td style={tdStyle}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleOne(report.id)}
                    aria-label={`Select report for ${report.name || report.domain}`}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
                <td style={{ ...tdStyle, maxWidth: '280px' }}>
                  <div
                    style={{ fontWeight: 600, color: T.gray800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    // The scanner and scoring builds are the only thing that
                    // tells two identical scans of one domain apart, so they
                    // stay reachable here rather than occupying a column that
                    // wrapped to four lines in every row.
                    title={`${report.url}\nScanner ${report.scannerVersion} · Scoring ${report.scoringVersion}`}
                  >
                    {report.name || report.domain}
                  </div>
                  {path && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: T.gray400,
                        marginTop: '2px',
                        fontFamily: T.fontMono,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {path}
                    </div>
                  )}
                </td>
                <td style={tdStyle}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '46px',
                      padding: '2px 8px',
                      borderRadius: `${T.radiusFull}px`,
                      backgroundColor: badge.bg,
                      color: badge.color,
                      fontFamily: T.fontMono,
                      fontWeight: 700,
                      fontSize: '13px',
                    }}
                  >
                    {badge.label}
                  </span>
                </td>
                <td style={tdStyle}>
                  <StatusBadge status={report.overallStatus} />
                </td>
                {showInitiatedBy && (
                  <td style={tdStyle}>
                    {report.userId ? (
                      <a
                        href={`#/admin/users/${report.userId}`}
                        title={report.userEmail || report.userId}
                        style={{
                          color: T.cobalt600,
                          textDecoration: 'none',
                          fontWeight: 500,
                          display: 'block',
                          maxWidth: '180px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {report.userEmail || report.userId}
                      </a>
                    ) : (
                      <span style={{ color: T.gray400 }}>Anonymous</span>
                    )}
                  </td>
                )}
                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                  <div>{formatDate(report.createdAt)}</div>
                  <div style={{ fontSize: '12px', color: T.gray400 }}>{formatTime(report.createdAt)}</div>
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                    <button onClick={() => onView(report.id)} style={primaryActionStyle}>
                      View
                    </button>
                    <button onClick={() => onShare(report.id, report.url ?? report.id)} style={quietActionStyle}>
                      Share
                    </button>
                    <button
                      onClick={() => onDelete(report.id)}
                      style={{ ...quietActionStyle, color: T.error }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// One primary action per row; Share and Delete recede to text so the rightmost
// column stops reading as a wall of tinted pills.
const primaryActionStyle: React.CSSProperties = {
  padding: '5px 12px',
  fontSize: '13px',
  color: T.cobalt600,
  backgroundColor: T.cobalt50,
  border: `1px solid ${T.cobalt200}`,
  borderRadius: `${T.radiusMd}px`,
  cursor: 'pointer',
  fontWeight: 600,
};

const quietActionStyle: React.CSSProperties = {
  padding: '5px 8px',
  fontSize: '13px',
  color: T.gray500,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: `${T.radiusMd}px`,
  cursor: 'pointer',
  fontWeight: 500,
};
